<script setup>
import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {announcements} from '@/config/announcements';
import {allowedBankID} from '@/helper';

/**
 * Zobrazí aktuálně platná oznámení ze src/config/announcements.js.
 *
 * Komponenta se vkládá jen na vybraná místa (login, nástěnka klienta a místa, kde se používá
 * Bank iD) - nejde o globální banner. Pokud žádné oznámení neplatí, nic nevykreslí.
 *
 * Props:
 * - onlyBankId: zobrazit jen oznámení vázaná na Bank iD (místa, kde se Bank iD přímo používá;
 *   obecná oznámení tam nepatří)
 */
const props = defineProps({
  onlyBankId: {type: Boolean, default: false},
});

const {locale} = useI18n();

// Aktuální čas jako ref, aby se oznámení skrylo / zobrazilo i na otevřené stránce bez reloadu
// (např. v 8:00 po konci platnosti). Přesnost na minutu je pro tento účel dostatečná.
const now = ref(Date.now());
let timer = null;
onMounted(() => {
  timer = setInterval(() => {
    now.value = Date.now();
  }, 60000);
});
onBeforeUnmount(() => clearInterval(timer));

const activeAnnouncements = computed(() => announcements.filter(item => {
  if (props.onlyBankId && !item.requiresBankId) {
    return false;
  }
  // Bank iD oznámení nemá smysl u tenantů bez Bank iD (SK)
  if (item.requiresBankId && !allowedBankID()) {
    return false;
  }
  return now.value >= Date.parse(item.showFrom) && now.value < Date.parse(item.showTo);
}));

// Text v jazyce aplikace; pro neznámý jazyk fallback na češtinu
const messageFor = (item) => item.message[locale.value] ?? item.message.cs;
</script>

<template>
  <div v-for="item of activeAnnouncements" :key="item.id" class="alert alert-warning app-announcement">
    <div class="alert-icon">
      <i class="mdi mdi-alert-outline"></i>
    </div>
    <!-- v-html: text pochází výhradně z konfigurace v kódu (ne od uživatele), obsahuje jen <strong> -->
    <div class="alert-content" v-html="messageFor(item)"></div>
  </div>
</template>

<style scoped>
/* Globální styl odsazuje text od absolutně pozicované ikony jen u .alert-info,
   pro varovné oznámení ho proto doplňujeme zde. */
.app-announcement .alert-content {
  padding-left: 50px;
}
</style>
